// ============================================================================
// dreFormat — regras PURAS da tela da DRE (Financeiro Fase 1d, 06/10/2026)
// ============================================================================
// A DRE vem PRONTA do servidor (/api/fin op=dre | dre-versao), em CENTAVOS com
// sinal (receita +, custo −) e pct = % da receita da coluna (ou null). Aqui só
// se FORMATA e se COMPARA — nenhum número é recalculado na tela:
//   · formatação de centavos/percentual/competência;
//   · etiquetas: rótulo curto, gravidade (o que pede atenção);
//   · checklist com as justificativas digitadas (o que a versão vai virar);
//   · diferença entre duas DREs (versões ou prévia × versão);
//   · CSV (DRE e títulos) e o documento de impressão;
//   · série da tendência da margem.
// Revisão 06/10/2026: a checagem (j) (reembolso interno) também bloqueia e se
// justifica; CSV neutraliza fórmula (texto que começa com = + − @ vira texto
// no Excel); a impressão mostra as checagens JUSTIFICADAS e, numa versão, o
// motivo e o hash.
// Testes: tests/dre-tela.test.mjs.
// ============================================================================

export const CHECAGENS_JUSTIFICAVEIS = ['a', 'b', 'd', 'e', 'f', 'g', 'j'];
export const MIN_JUSTIFICATIVA = 10;
const MESES_CURTOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MESES_LONGOS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// ─── Números ────────────────────────────────────────────────────────────────
const nf2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/** centavos → "R$ 1.234,56" (negativo: "−R$ 1.234,56"). null/undefined → "—". */
export function fmtCentavos(c, { zeroTraco = false } = {}) {
  if (c === null || c === undefined || c === '' || !Number.isFinite(Number(c))) return '—';
  const n = Number(c);
  if (zeroTraco && n === 0) return '—';
  const s = `R$ ${nf2.format(Math.abs(n) / 100)}`;
  return n < 0 ? `−${s}` : s;
}

/** centavos → "R$ 1,2 mil" / "R$ 12 mil" (eixos, chips). */
export function fmtCentavosCurto(c) {
  if (c === null || c === undefined || !Number.isFinite(Number(c))) return '—';
  const n = Number(c) / 100;
  const abs = Math.abs(n);
  const sinal = n < 0 ? '−' : '';
  if (abs >= 1e6) return `${sinal}R$ ${nf2.format(abs / 1e6)} mi`;
  if (abs >= 1e4) return `${sinal}R$ ${nf0.format(abs / 1e3)} mil`;
  return `${sinal}R$ ${nf0.format(abs)}`;
}

/** 7.44 → "7,44%"; null → "—". */
export function fmtPct(p, casas = 2) {
  if (p === null || p === undefined || !Number.isFinite(Number(p))) return '—';
  const n = Number(p);
  const s = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }).format(Math.abs(n));
  // −0,0% não existe: o sinal só aparece se sobrar algo depois do arredondamento
  const negativo = n < 0 && Number(Math.abs(n).toFixed(casas)) !== 0;
  return `${negativo ? '−' : ''}${s}%`;
}

/** Diferença em pontos percentuais: "+1,20 p.p." */
export function fmtPp(d) {
  if (d === null || d === undefined || !Number.isFinite(Number(d))) return '—';
  const n = Number(d);
  const s = nf2.format(Math.abs(n));
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${s} p.p.`;
}

/** Diferença de centavos com sinal explícito: "+R$ 10,00" / "−R$ 10,00" / "R$ 0,00". */
export function fmtDelta(c) {
  if (c === null || c === undefined || !Number.isFinite(Number(c))) return '—';
  const n = Number(c);
  if (n === 0) return 'R$ 0,00';
  return `${n > 0 ? '+' : '−'}R$ ${nf2.format(Math.abs(n) / 100)}`;
}

// centavos → "1234,56" (CSV pt-BR, sem separador de milhar)
const csvNum = (c) => (c === null || c === undefined || !Number.isFinite(Number(c)) ? '' : (Number(c) / 100).toFixed(2).replace('.', ','));
const csvPct = (p) => (p === null || p === undefined || !Number.isFinite(Number(p)) ? '' : Number(p).toFixed(2).replace('.', ','));

// ─── Datas / competência ───────────────────────────────────────────────────
export const competenciaValida = (c) => /^\d{4}-(0[1-9]|1[0-2])$/.test(String(c || ''));

/** 'AAAA-MM' + n meses. */
export function somarMes(comp, n) {
  const [y, m] = String(comp).split('-').map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}

/** Competência padrão da tela = mês anterior a hoje (AAAA-MM-DD, fuso SP). */
export const competenciaPadrao = (hoje) => somarMes(String(hoje).slice(0, 7), -1);

/** '2026-07' → 'Jul/26' */
export function compCurta(comp) {
  if (!competenciaValida(comp)) return String(comp || '—');
  const [y, m] = comp.split('-');
  return `${MESES_CURTOS[Number(m) - 1]}/${y.slice(2)}`;
}

/** '2026-07' → 'julho de 2026' */
export function compLonga(comp) {
  if (!competenciaValida(comp)) return String(comp || '—');
  const [y, m] = comp.split('-');
  return `${MESES_LONGOS[Number(m) - 1]} de ${y}`;
}

/** 'AAAA-MM-DD…' → 'DD/MM/AAAA' */
export function dataBR(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

/** ISO com hora → 'DD/MM/AAAA HH:MM' (fuso de SP). */
export function dataHoraBR(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(d).replace(',', '');
  } catch {
    return dataBR(iso);
  }
}

// ─── Colunas ────────────────────────────────────────────────────────────────
/** Nome da coluna ('rede' → 'Rede'). */
export function nomeColuna(dre, col) {
  if (col === 'rede') return 'Rede';
  const u = (dre?.unidades || []).find(x => x.id === col);
  return u?.nome || col;
}

/** Colunas de unidade (sem 'rede'), na ordem do servidor. */
export const colunasUnidades = (dre) => (dre?.colunas || []).filter(c => c !== 'rede');

// ─── Grupo "Oscar Freire + Jardins" (09/10/2026) ───────────────────────────
// Na visão agrupada (padrão do servidor, visao=grupo) a DRE tem a coluna do
// grupo no lugar das duas; `unidades` traz { id: grupo, nome, grupo: true,
// unidades: [...] } e as individuais continuam lá (para os nomes).
/** A coluna é de um grupo? → as unidades dele; senão null. */
export function unidadesDoGrupoNaDre(dre, col) {
  const u = (dre?.unidades || []).find(x => x.id === col);
  return u && u.grupo && Array.isArray(u.unidades) ? u.unidades : null;
}
/** Colunas de unidade com os grupos ABERTOS nas unidades reais (para as tabelas de conferência, que são por unidade). */
export function colunasReais(dre) {
  const out = [];
  for (const c of colunasUnidades(dre)) {
    const us = unidadesDoGrupoNaDre(dre, c);
    if (us) { for (const u of us) if (!out.includes(u)) out.push(u); } else if (!out.includes(c)) out.push(c);
  }
  return out;
}
/** A DRE está na visão agrupada? (tem coluna de grupo) */
export const dreAgrupada = (dre) => colunasUnidades(dre).some(c => !!unidadesDoGrupoNaDre(dre, c));

// ─── Etiquetas ──────────────────────────────────────────────────────────────
// Rótulo curto (chip) e gravidade. 'atencao' = número que ainda não é o real
// (estimado, a validar, sem fotografia…); 'problema' = falta/erro de dado.
// As informativas (real, fotografia, núcleo, rateio, JB) não aparecem na
// célula — só no detalhe dos títulos.
export const ETIQUETA_CURTA = {
  estimado: 'estimado',
  // 17/10/2026: imposto sem estimativa; sem guia lançada a linha 1 fica em zero com este aviso
  nao_lancado: 'não lançado',
  a_validar: 'a validar',
  sem_fotografia: 'sem foto',
  reconstruida: 'reconstruída',
  nao_baixado: 'não baixado',
  a_classificar: 'a classificar',
  sem_receita: 'sem receita',
  fonte_alternativa: 'fonte alt.',
  sem_comissao_apurada: 'sem comissão',
  comissao_descontada: 'com. descontada',
  pessoa_nao_identificada: 'sem pessoa',
  sem_unidade: 'sem unidade',
  sem_parametro: 'sem parâmetro',
  real: 'real',
  fotografia: 'fotografia',
  nucleo: 'núcleo',
  rateio: 'rateio',
  jb_educacao: 'JB Educação',
  com_ressalvas: 'com ressalvas',
  previa: 'prévia',
  pagamento_da_fotografia: 'pagamento',
  fora_da_fotografia: 'fora da foto',
  reconhecido_pelo_texto: 'reconhecido',
  reembolso_interno: 'reembolso interno',
  // 16/10/2026 — Fluxo de Caixa pela conta Stone (FIN_FLUXO_BANCO; api/_lib/fin-fluxo.js
  // ETIQUETAS_FLUXO). Só códigos NOVOS: a DRE não emite nenhum deles, nada muda nela.
  sem_extrato: 'sem extrato',
  parcial: 'parcial',
  parcial_inicio: 'começa depois do dia 1',
  zerada_antes: 'zerada antes',
  sem_saldo: 'sem saldo',
  futuro: 'mês futuro',
  sem_par: 'sem par',
  conta_fora_do_sistema: 'fora do sistema',
  revisar: 'revisar',
  conta_cancelada: 'conta cancelada',
  socio_locador: 'sócio-locador',
  automatico: 'automático',
  sem_coleta: 'sem dado do Avec',
  sem_agenda: 'sem agenda',
  sem_contagem: 'sem contagem',
  rede_parcial: 'rede parcial',
  // 17/10/2026 — PIX para sócio a classificar e o bloco "Disponível para retirada" do Fluxo
  pix_socio: 'PIX para sócio',
  dre_nao_fechada: 'DRE não fechada',
  dre_nao_lida: 'DRE não lida',
  reserva_nao_definida: 'reserva não definida',
  reserva_nao_lida: 'reserva não lida',
  // 22/10/2026 (lapidação, etapa C): devolução que segue o débito e a trava do lucro da DRE na retirada
  devolucao: 'devolução',
  dre_com_ressalvas: 'DRE com ressalvas',
  dre_mudou: 'DRE mudou',
  dre_sem_conferencia: 'DRE sem conferência',
  dre_nao_conferida: 'DRE não conferida',
  imposto_nao_lancado: 'imposto não lançado',
  // 22/10/2026 (revisão 1): o extrato deste mês até o dia 20 ainda pendente (a trava do lucro na retirada)
  extrato_mes_pendente: 'extrato do mês pendente',
};
const PROBLEMA = new Set(['a_classificar', 'sem_receita', 'sem_parametro', 'sem_unidade', 'pessoa_nao_identificada',
  // Fluxo de Caixa (16/10/2026): falta dado do banco
  'sem_extrato', 'sem_saldo', 'sem_par', 'conta_cancelada',
  // 17/10/2026: a fonte do disponível para retirada não foi lida
  'dre_nao_lida', 'reserva_nao_lida',
  // 22/10/2026 (etapa C): não deu para conferir se a DRE mudou depois de fechada
  'dre_nao_conferida']);
const ATENCAO = new Set(['estimado', 'nao_lancado', 'a_validar', 'sem_fotografia', 'reconstruida', 'nao_baixado', 'fonte_alternativa', 'sem_comissao_apurada',
  'comissao_descontada', 'fora_da_fotografia', 'reconhecido_pelo_texto', 'reembolso_interno',
  // Fluxo de Caixa (16/10/2026): número que ainda não é o do mês inteiro / pede revisão
  'parcial', 'parcial_inicio', 'futuro', 'conta_fora_do_sistema', 'revisar', 'sem_coleta', 'sem_agenda', 'sem_contagem', 'rede_parcial',
  // 17/10/2026: PIX para sócio (retirada ou reembolso?) e o que ainda falta para o disponível para retirada
  'pix_socio', 'dre_nao_fechada', 'reserva_nao_definida',
  // 22/10/2026 (etapa C): a DRE ainda não serve para a retirada
  'dre_com_ressalvas', 'dre_mudou', 'dre_sem_conferencia', 'imposto_nao_lancado',
  // 22/10/2026 (revisão 1)
  'extrato_mes_pendente']);

/** 'problema' | 'atencao' | 'info' */
export function gravidadeEtiqueta(e) {
  if (PROBLEMA.has(e)) return 'problema';
  if (ATENCAO.has(e)) return 'atencao';
  return 'info';
}

/** Só as etiquetas que pedem olho (para a célula da tabela), problema primeiro. */
export function etiquetasDeAtencao(lista) {
  return [...new Set(Array.isArray(lista) ? lista : [])]
    .filter(e => gravidadeEtiqueta(e) !== 'info')
    .sort((a, b) => (gravidadeEtiqueta(a) === 'problema' ? 0 : 1) - (gravidadeEtiqueta(b) === 'problema' ? 0 : 1) || a.localeCompare(b));
}

/** Texto completo da etiqueta (dicionário do servidor, senão o curto). */
export const textoEtiqueta = (e, dic = {}) => dic?.[e] || ETIQUETA_CURTA[e] || e;

// ─── Itens de um detalhe ────────────────────────────────────────────────────
/** Itens de um detalhe numa coluna; 'rede' = todas as unidades (com unit_col). */
// 16/10/2026 (Fluxo de Caixa): `colunas_rede` (se vier) diz quais colunas compõem a Rede (só as
// contas com extrato no mês; a soma de Oscar + Jardins não entra de novo). A DRE não traz o campo.
export function itensDaColuna(dre, det, col) {
  if (!det?.itens) return [];
  if (col !== 'rede') return (det.itens[col] || []).map(i => ({ ...i, unit_col: col }));
  const out = [];
  const daRede = Array.isArray(dre?.colunas_rede) ? dre.colunas_rede : colunasUnidades(dre);
  for (const u of daRede) for (const i of det.itens[u] || []) out.push({ ...i, unit_col: u });
  return out;
}

/** Categoria do núcleo com o mesmo código do detalhe da linha 8 (títulos de origem). */
export function categoriaDoNucleo(dre, codigoDetalhe) {
  return (dre?.nucleo?.categorias || []).find(n => n.codigo === codigoDetalhe) || null;
}

// ─── Checklist com as justificativas digitadas ──────────────────────────────
// O checklist da PRÉVIA vem sem justificativas. Com o texto digitado na tela
// (≥ 10 caracteres, só a/b/d/e/f/g/j), a checagem conta como ok — igual ao
// servidor na hora de gerar. Devolve o checklist "como vai sair".
export function checklistComJustificativas(checklist, justificativas = {}) {
  if (!checklist || !Array.isArray(checklist.itens)) return checklist;
  const itens = checklist.itens.map(i => {
    const m = /^checagem_([a-j])$/.exec(i.codigo || '');
    const letra = m?.[1];
    if (!letra || !CHECAGENS_JUSTIFICAVEIS.includes(letra) || i.ok || !(Number(i.quantidade) > 0)) return i;
    const t = String(justificativas?.[letra] ?? '').trim();
    return t.length >= MIN_JUSTIFICATIVA ? { ...i, ok: true, justificativa: t, justificada_na_tela: true } : i;
  });
  const pendentes = itens.filter(i => i.bloqueia && !i.ok).map(i => i.codigo);
  return { ...checklist, itens, pendentes, ok: pendentes.length === 0, status: pendentes.length === 0 ? 'fechada' : 'com_ressalvas' };
}

/** Justificativas válidas para enviar (só letras justificáveis, ≥ 10 caracteres). */
export function justificativasParaEnviar(justificativas = {}) {
  const out = {};
  for (const l of CHECAGENS_JUSTIFICAVEIS) {
    const t = String(justificativas?.[l] ?? '').trim();
    if (t.length >= MIN_JUSTIFICATIVA) out[l] = t.slice(0, 500);
  }
  return out;
}

// ─── Diferença entre duas DREs ──────────────────────────────────────────────
// antes/depois = objetos DRE (versão.linhas ou prévia.dre). Por linha e por
// detalhe: { antes, depois, delta } em cada coluna; `mudou` quando algum delta
// ≠ 0. Linha/detalhe que só existe de um lado conta com 0 do outro.
export function diffDre(antes, depois) {
  const A = antes || {}; const B = depois || {};
  const colunas = [...new Set([...(B.colunas || []), ...(A.colunas || [])])];
  // 'rede' sempre no fim
  colunas.sort((x, y) => (x === 'rede') - (y === 'rede'));
  const mapA = new Map((A.linhas || []).map(l => [l.codigo, l]));
  const mapB = new Map((B.linhas || []).map(l => [l.codigo, l]));
  const codigos = [...new Set([...(B.linhas || []).map(l => l.codigo), ...(A.linhas || []).map(l => l.codigo)])];
  const celula = (la, lb, c) => {
    const a = Number(la?.valores?.[c] ?? 0) || 0;
    const b = Number(lb?.valores?.[c] ?? 0) || 0;
    return { antes: a, depois: b, delta: b - a };
  };
  let celulasMudaram = 0;
  const linhas = codigos.map(cod => {
    const la = mapA.get(cod); const lb = mapB.get(cod);
    const valores = Object.fromEntries(colunas.map(c => [c, celula(la, lb, c)]));
    const mudouL = colunas.some(c => valores[c].delta !== 0);
    if (mudouL) celulasMudaram += colunas.filter(c => valores[c].delta !== 0).length;
    const dA = new Map((la?.detalhe || []).map(d => [d.codigo, d]));
    const dB = new Map((lb?.detalhe || []).map(d => [d.codigo, d]));
    const dets = [...new Set([...(lb?.detalhe || []).map(d => d.codigo), ...(la?.detalhe || []).map(d => d.codigo)])];
    const detalhe = dets.map(dc => {
      const da = dA.get(dc); const db = dB.get(dc);
      const v = Object.fromEntries(colunas.map(c => [c, celula(da, db, c)]));
      return {
        codigo: dc, nome: db?.nome || da?.nome || dc, valores: v,
        mudou: colunas.some(c => v[c].delta !== 0), so_antes: !db, so_depois: !da,
      };
    }).filter(d => d.mudou || d.so_antes || d.so_depois);
    return { codigo: cod, nome: lb?.nome || la?.nome || cod, tipo: lb?.tipo || la?.tipo, valores, mudou: mudouL, detalhe };
  });
  const resumo = Object.fromEntries(colunas.map(c => {
    const ra = A.resumo?.[c] || {}; const rb = B.resumo?.[c] || {};
    const m = (k) => (ra[k] == null || rb[k] == null ? null : Math.round((rb[k] - ra[k]) * 100) / 100);
    return [c, {
      margem_operacional: { antes: ra.margem_operacional_pct ?? null, depois: rb.margem_operacional_pct ?? null, delta_pp: m('margem_operacional_pct') },
      resultado_operacional: { antes: ra.resultado_operacional_cents ?? null, depois: rb.resultado_operacional_cents ?? null,
        delta: ra.resultado_operacional_cents == null || rb.resultado_operacional_cents == null ? null : rb.resultado_operacional_cents - ra.resultado_operacional_cents },
    }];
  }));
  return { colunas, linhas, resumo, mudou: celulasMudaram > 0, celulas_mudaram: celulasMudaram };
}

// ─── CSV ────────────────────────────────────────────────────────────────────
// Texto que começa com = + - @ (ou tab/CR) viraria FÓRMULA no Excel/Sheets
// (CWE-1236): a descrição de uma saída do caixa é texto livre da recepção. O
// apóstrofo na frente faz a planilha mostrar como texto. Os números (csvNum,
// csvPct) não passam por aqui.
export const csvCel = (v) => {
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[";\n\r\t]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
// números já formatados (csvNum/csvPct, "-1234,56") vão crus; o resto passa pelo csvCel
const NUM_CSV = /^-?\d+(,\d+)?$/;
const csvLinha = (arr) => arr.map(v => (typeof v === 'string' && NUM_CSV.test(v) ? v : csvCel(v))).join(';');

/** Rótulo do estado da DRE na tela/arquivo. */
export function rotuloEstado({ previa = true, versao = null, status = null } = {}) {
  if (previa) return 'PRÉVIA (não gravada)';
  const st = status === 'fechada' ? 'fechada' : status === 'com_ressalvas' ? 'com ressalvas' : (status || '');
  return `Versão ${versao ?? '?'}${st ? ` (${st})` : ''}`;
}

/** DRE → CSV (pt-BR, ';', com BOM): linhas macro e detalhes, R$ e % por coluna. */
export function dreParaCsv(dre, { estado = 'PRÉVIA (não gravada)', etiquetas = {} } = {}) {
  const cols = dre?.colunas || [];
  const out = [];
  out.push(csvLinha(['DRE gerencial', `Competência ${compCurta(dre?.competencia)}`, estado]));
  out.push(csvLinha(['Nível de evidência', dre?.nivel_evidencia_texto || dre?.nivel_evidencia || '']));
  out.push(csvLinha(['Motor', dre?.motor || '']));
  out.push(csvLinha(['Valores em R$; custo negativo; % = da receita da coluna']));
  out.push('');
  const cab = ['Código', 'Linha'];
  for (const c of cols) cab.push(`${nomeColuna(dre, c)} (R$)`, `${nomeColuna(dre, c)} (%)`);
  cab.push('Etiquetas');
  out.push(csvLinha(cab));
  const etqTxt = (et) => [...new Set(cols.flatMap(c => et?.[c] || []))].map(e => textoEtiqueta(e, etiquetas)).join(' | ');
  for (const L of dre?.linhas || []) {
    const r = [L.codigo, L.nome];
    for (const c of cols) r.push(csvNum(L.valores?.[c]), csvPct(L.pct?.[c]));
    r.push(etqTxt(L.etiquetas));
    out.push(csvLinha(r));
    for (const D of L.detalhe || []) {
      const rd = [`${L.codigo}/${D.codigo}`, `  · ${D.nome}`];
      for (const c of cols) rd.push(csvNum(D.valores?.[c]), csvPct(D.pct?.[c]));
      rd.push(etqTxt(D.etiquetas));
      out.push(csvLinha(rd));
    }
  }
  return '﻿' + out.join('\n');
}

/** Títulos/itens da DRE → CSV (uma linha por item, por unidade). */
export function dreItensParaCsv(dre, { etiquetas = {} } = {}) {
  const out = [];
  out.push(csvLinha(['Competência', 'Linha', 'Detalhe', 'Coluna', 'Tipo', 'Título/descrição', 'Fornecedor', 'Unidade do título',
    'Categoria', 'Estado', 'Data', 'Vencimento', 'Pagamento', 'Valor (R$)', 'Contribuição na DRE (R$)', 'Etiquetas']));
  for (const L of dre?.linhas || []) {
    for (const D of L.detalhe || []) {
      for (const u of colunasUnidades(dre)) {
        for (const i of D.itens?.[u] || []) {
          out.push(csvLinha([
            dre.competencia, `${L.codigo} ${L.nome}`, D.nome, nomeColuna(dre, u), i.tipo || 'titulo',
            i.titulo || i.descricao || i.nome || i.fonte || '', i.fornecedor || '', i.unit_id || '',
            i.categoria_valor || i.categoria || '', i.estado || '', i.data || '', i.vencimento || '', i.data_pagamento || '',
            csvNum(i.valor_cents), csvNum(i.contribuicao_cents),
            (i.etiquetas || []).map(e => textoEtiqueta(e, etiquetas)).join(' | '),
          ]));
        }
      }
    }
  }
  return '﻿' + out.join('\n');
}

// ─── Tendência ──────────────────────────────────────────────────────────────
// pontos do op=dre-tendencia → linhas do gráfico: { competencia, rotulo,
// fonte, versao, status, [col]: valor } (metrica = campo do resumo).
export function serieTendencia(tend, metrica = 'margem_operacional_pct') {
  const cols = tend?.colunas || [];
  return (tend?.pontos || []).map(p => {
    const linha = { competencia: p.competencia, rotulo: compCurta(p.competencia), fonte: p.fonte, versao: p.versao, status: p.status };
    // prévia: o que ela é (mês sem custo lançado não é margem — é receita menos estimativa)
    if (p.fonte === 'previa' && p.titulos_custo !== undefined) {
      linha.titulos_custo = p.titulos_custo;
      linha.sem_custos = !!p.sem_custos;
      linha.status_previsto = p.status_previsto || null;
      linha.pendentes = Array.isArray(p.pendentes) ? p.pendentes.length : null;
    }
    for (const c of cols) {
      const v = p.resumo?.[c]?.[metrica];
      linha[c] = v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v);
    }
    return linha;
  });
}

// ─── Impressão ──────────────────────────────────────────────────────────────
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

/** Documento HTML (A4 paisagem) da DRE para imprimir/salvar PDF. */
export function documentoImpressaoDre({ dre, estado = 'PRÉVIA (não gravada)', checklist = null, etiquetas = {}, geradoEm = null, rodape = '', versao = null }) {
  const cols = dre?.colunas || [];
  const cab = cols.map(c => `<th class="num">${esc(nomeColuna(dre, c))}</th>`).join('');
  const celula = (v, p, forte = false) => `<td class="num${forte ? ' forte' : ''}${Number(v) < 0 ? ' neg' : ''}">${esc(fmtCentavos(v))}<span class="pct">${esc(fmtPct(p, 1))}</span></td>`;
  const corpo = (dre?.linhas || []).map(L => {
    const sub = L.tipo === 'subtotal' || L.tipo === 'receita';
    const etq = etiquetasDeAtencao(cols.flatMap(c => L.etiquetas?.[c] || []));
    const marca = etq.length ? ` <span class="etq">${esc(etq.map(e => ETIQUETA_CURTA[e] || e).join(', '))}</span>` : '';
    const linhaMacro = `<tr class="${sub ? 'sub' : 'macro'}"><td>${esc(L.codigo !== 'receita' && !String(L.codigo).startsWith('resultado') ? `${L.codigo}. ` : '')}${esc(L.nome)}${marca}</td>${cols.map(c => celula(L.valores?.[c], L.pct?.[c], sub)).join('')}</tr>`;
    const dets = (L.detalhe || []).filter(D => cols.some(c => Number(D.valores?.[c]) !== 0))
      .map(D => `<tr class="det"><td>${esc(D.nome)}</td>${cols.map(c => celula(D.valores?.[c], D.pct?.[c])).join('')}</tr>`).join('');
    return linhaMacro + dets;
  }).join('');
  const pend = checklist && Array.isArray(checklist.itens)
    ? checklist.itens.filter(i => i.bloqueia && !i.ok).map(i => `<li><strong>${esc(i.titulo)}</strong> — ${esc(i.detalhe || '')}</li>`).join('')
    : '';
  // justificada = ok:true no checklist; no papel ela NÃO some (quem lê precisa saber que não fechou limpo)
  const justificadas = checklist && Array.isArray(checklist.itens)
    ? checklist.itens.filter(i => i.ok && i.justificativa && Number(i.quantidade) > 0)
      .map(i => `<li><strong>${esc(i.titulo)}</strong> — ${esc(`${i.quantidade} item(ns)`)}. Justificativa: “${esc(i.justificativa)}”</li>`).join('')
    : '';
  const metaVersao = versao
    ? `<p class="meta">Versão ${esc(versao.versao ?? '?')}${versao.status ? ` (${esc(versao.status === 'fechada' ? 'fechada' : 'com ressalvas')})` : ''}`
      + `${versao.motivo ? ` · Motivo: “${esc(versao.motivo)}”` : ''}${versao.gerada_por ? ` · por ${esc(versao.gerada_por)}` : ''}`
      + `${versao.hash ? ` · hash ${esc(String(versao.hash).slice(0, 16))}…` : ''}</p>`
    : '';
  const usadas = [...new Set((dre?.linhas || []).flatMap(L => [...cols.flatMap(c => L.etiquetas?.[c] || []), ...(L.detalhe || []).flatMap(D => cols.flatMap(c => D.etiquetas?.[c] || []))]))]
    .filter(e => gravidadeEtiqueta(e) !== 'info');
  const legenda = usadas.map(e => `<li><strong>${esc(ETIQUETA_CURTA[e] || e)}</strong>: ${esc(textoEtiqueta(e, etiquetas))}</li>`).join('');
  const avisos = (dre?.avisos || []).map(a => `<li>${esc(a)}</li>`).join('');
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>DRE ${esc(compCurta(dre?.competencia))} — ${esc(estado)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #0F1B2D; margin: 16px; font-size: 11px; background: #fff; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .meta { color: #51627B; margin-bottom: 10px; }
  .estado { display: inline-block; padding: 2px 8px; border-radius: 999px; border: 1px solid #41659E; color: #29426F; font-weight: 600; font-size: 10px; }
  .estado.previa { border-color: #D97706; color: #92400E; background: #FFFBEB; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 3px 6px; border-bottom: 1px solid #E2E8F0; vertical-align: top; }
  th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: .06em; color: #51627B; border-bottom: 2px solid #CBD5E1; }
  .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .pct { display: block; color: #64748B; font-size: 8.5px; }
  tr.sub td { font-weight: 700; background: #EEF3FA; border-top: 1px solid #BACBE4; }
  tr.macro td { font-weight: 600; }
  tr.det td { color: #334155; font-size: 10px; }
  tr.det td:first-child { padding-left: 18px; }
  td.neg { color: #9F1239; }
  .etq { font-weight: 500; font-size: 8.5px; color: #92400E; background: #FFFBEB; border: 1px solid #FCD34D; border-radius: 6px; padding: 0 4px; margin-left: 4px; }
  h2 { font-size: 12px; margin: 14px 0 4px; }
  ul { margin: 0; padding-left: 16px; }
  li { margin: 1px 0; }
  .botoes { margin-bottom: 10px; display: flex; gap: 8px; }
  .botoes button { font: inherit; padding: 6px 12px; border-radius: 8px; border: 1px solid #CBD5E1; background: #fff; cursor: pointer; }
  .botoes .imprimir { background: #41659E; color: #fff; border-color: #41659E; }
  @media print { .botoes { display: none; } body { margin: 0; } }
</style></head><body>
<div class="botoes"><button onclick="window.close()">Fechar</button><button class="imprimir" onclick="window.print()">Imprimir / Salvar PDF</button></div>
<h1>DRE gerencial — ${esc(compLonga(dre?.competencia))}</h1>
<div class="meta"><span class="estado${/PRÉVIA/.test(estado) ? ' previa' : ''}">${esc(estado)}</span>
 · ${esc(dre?.nivel_evidencia_texto || '')} · ${esc(dre?.motor || '')}${geradoEm ? ` · ${esc(geradoEm)}` : ''}</div>
${metaVersao}
<table><thead><tr><th>Linha</th>${cab}</tr></thead><tbody>${corpo}</tbody></table>
<p class="meta">Valores em R$ (custo negativo). Percentual = da receita da própria coluna. Rede = soma das unidades (o núcleo já rateado).</p>
${pend ? `<h2>Pendências do checklist</h2><ul>${pend}</ul>` : ''}
${justificadas ? `<h2>Checagens justificadas (não zeraram)</h2><ul>${justificadas}</ul>` : ''}
${legenda ? `<h2>Etiquetas</h2><ul>${legenda}</ul>` : ''}
${avisos ? `<h2>Avisos do cálculo</h2><ul>${avisos}</ul>` : ''}
${rodape ? `<p class="meta">${esc(rodape)}</p>` : ''}
</body></html>`;
}

// (d) do grupo, regra 'menos_que_o_normal' (fin-checagens.js manda motivo,
// quantidade e quantidade_normal): o motivo pronto do servidor; sem ele, monta.
export function textoMenosQueONormal(it, nomeGrupo = '') {
  if (it?.motivo) return it.motivo;
  const n = Number.isSafeInteger(it?.quantidade) ? it.quantidade : null;
  const normal = Number.isSafeInteger(it?.quantidade_normal) ? it.quantidade_normal : null;
  if (n == null || normal == null) return `menos títulos que o normal neste mês${nomeGrupo ? ` (${nomeGrupo})` : ''}`;
  return `${n} ${n === 1 ? 'título' : 'títulos'} neste mês; o normal é ${normal}${nomeGrupo ? ` (${nomeGrupo})` : ''}`;
}
